import React, { useCallback, useEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link, Outlet, useLocation } from "react-router-dom";

import { useUser } from "../hook/useUser";
import { signOut } from "../services/auth";
import { sidebarMenu } from "../config/sidebarMenu";
import MySuhbatLogo from "./MySuhbatLogo";
import Rail from "./Rail";
import Avatar from "../components/ui/Avatar";
import CommandPalette from "../components/ui/CommandPalette";

/**
 * The shell: a rail, a top bar, and the page.
 *
 * Rewritten down from the ERP layout this came from, which carried a two-row
 * header, a notifications socket, a sound cue and a row of department
 * shortcuts. None of that applies here -- MySuhbat has no notifications in V1
 * (the spec puts push in V2), so there is no bell, no websocket and no audio
 * element. The design's own classes are untouched; only the arrangement
 * differs, and the rail is the single structural change.
 *
 * Since 2026-10-22 the rail has two widths and three behaviours:
 *   * >= 1280px  wide by default, icon-only if you fold it;
 *   * 992-1279px icon-only by default, wide if you unfold it;
 *   * < 992px    a drawer over the page, opened from the bar.
 * Folding or unfolding is remembered per browser (localStorage) and wins over
 * the default from then on. Same shell as the staff panel, in the cabinet's
 * teal; the bar carries a quick search (Ctrl+K, CommandPalette) and the rail
 * names the client system this cabinet is for, with your access level.
 */

const RAIL_KEY = "ms.rail";

const readRailPref = () => {
  try {
    const v = localStorage.getItem(RAIL_KEY);
    return v === "wide" || v === "compact" ? v : null;
  } catch {
    return null;
  }
};

/** Which of the three widths the window is in, kept current on resize. */
const useViewport = () => {
  const get = () =>
    window.innerWidth < 992 ? "drawer" : window.innerWidth < 1280 ? "medium" : "large";
  const [size, setSize] = useState(get);
  useEffect(() => {
    const onResize = () => setSize(get());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return size;
};

/**
 * Pages reached from the chrome rather than the rail, so they are not in the
 * menu config but still have to name themselves in the bar.
 */
const CHROME_ROUTES = [{ to: "/profil", group: "Hisob", title: "Profil" }];

/** Where am I? Read off the same config the rail renders. */
const findCrumb = (pathname) => {
  const chrome = CHROME_ROUTES.find(
    (r) => pathname === r.to || pathname.startsWith(`${r.to}/`),
  );
  if (chrome) return { group: chrome.group, title: chrome.title };

  let current = "";
  for (const entry of sidebarMenu) {
    if (entry.type === "group") {
      current = entry.label;
      continue;
    }
    const hit = entry.end
      ? pathname === entry.to
      : pathname === entry.to || pathname.startsWith(`${entry.to}/`);
    if (hit) return { group: current, title: entry.label };
  }
  return { group: "", title: "" };
};

const ACCESS_LABELS = { admin: "boshqaruvchi", read: "faqat koʻrish" };

const MasterLayout = () => {
  const location = useLocation();
  const { user, loading } = useUser();

  const [railOpen, setRailOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const profileWrap = useRef(null);

  const viewport = useViewport();
  const [railPref, setRailPref] = useState(readRailPref);
  const drawer = viewport === "drawer";
  // The drawer is always the full rail: it is opened on purpose, over the page.
  const compact = !drawer && (railPref ? railPref === "compact" : viewport === "medium");

  const toggleRail = () => {
    const next = compact ? "wide" : "compact";
    setRailPref(next);
    try {
      localStorage.setItem(RAIL_KEY, next);
    } catch {
      /* private window: it just will not be remembered */
    }
  };

  const closePalette = useCallback(() => setPaletteOpen(false), []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const crumb = findCrumb(location.pathname);

  // A route change closes both: the rail because on a phone it covers the page
  // you just asked for, the menu because it is about the session, not the page.
  useEffect(() => {
    setRailOpen(false);
    setProfileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!profileOpen) return;
    const onDown = (e) => {
      if (!profileWrap.current?.contains(e.target)) setProfileOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setProfileOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [profileOpen]);

  /**
   * Nothing in the rail is hidden yet: a read-only grant sees every page and
   * loses only the edit controls on it. Kept as the one place that decision
   * would live, rather than scattering role checks through the menu.
   */
  const menuItemVisible = (entry) => {
    const roles = entry.roles || [];
    return roles.includes("all") || roles.includes(user?.role);
  };

  const grant = user?.memberships?.[0] ?? null;
  const displayName = loading ? "Yuklanmoqda…" : user?.full_name || user?.email || "Foydalanuvchi";
  const accessLabel = grant ? ACCESS_LABELS[grant.access] || grant.access : "";
  const displayRole = loading
    ? ""
    : grant
      ? `${grant.client_name} · ${accessLabel}`
      : "tizim biriktirilmagan";
  // What the quick search offers before anything is typed: every page.
  const pages = [
    ...sidebarMenu.reduce((acc, e) => {
      if (e.type === "group") acc.group = e.label;
      else if (menuItemVisible(e)) acc.list.push({ ...e, group: acc.group });
      return acc;
    }, { group: "", list: [] }).list,
    { label: "Profil", to: "/profil", icon: "tabler:user", group: "Hisob" },
  ];

  return (
    <section className="ms-shell">
      {/* The scrim only exists while the rail is over the page, below 992px.
          It closes the rail on a tap without swallowing clicks the rest of the
          time. */}
      {railOpen && (
        <div className="ms-rail-scrim" onClick={() => setRailOpen(false)} aria-hidden="true" />
      )}

      <aside
        className={`ms-rail${railOpen ? " is-open" : ""}${compact ? " is-compact" : ""}`}
      >
        <div className="ms-rail-head">
          <Link to="/" className="ms-brand" aria-label="MySuhbat — Umumiy koʻrinish">
            <MySuhbatLogo compact={compact} />
          </Link>
        </div>

        {!drawer && (
          <button
            type="button"
            className="ms-rail-edge"
            onClick={toggleRail}
            aria-label={compact ? "Menyuni kengaytirish" : "Menyuni yigʻish"}
            data-tip={compact ? "Kengaytirish" : "Yigʻish"}
          >
            <Icon icon="tabler:chevron-left" />
          </button>
        )}

        {/* Which client system this cabinet is for, and what you may do in it:
            the one thing the staff panel's rail has no need to say. */}
        {grant && (
          <div
            className="ms-rail-client"
            data-tip={compact ? `${grant.client_name} · ${accessLabel}` : undefined}
          >
            <span className="ms-rail-client-icon">
              <Icon icon="tabler:building" />
            </span>
            {!compact && (
              <span>
                <b>{grant.client_name}</b>
                <small>
                  <Icon icon={grant.access === "admin" ? "tabler:shield-check" : "tabler:eye"} />
                  {accessLabel}
                </small>
              </span>
            )}
          </div>
        )}

        <Rail
          itemVisible={menuItemVisible}
          compact={compact}
          onNavigate={() => setRailOpen(false)}
        />

        {/* Who is signed in, at the foot of the rail: the bar shows it too,
            but on a phone the bar hides the name and this is where you look. */}
        <Link
          to="/profil"
          className="ms-rail-me"
          aria-label={compact ? `Profil — ${displayName}` : undefined}
          data-tip={compact ? displayName : undefined}
        >
          {loading ? <div className="ms-avatar-skeleton" /> : <Avatar name={displayName} />}
          {!compact && (
            <span>
              <b>{displayName}</b>
              <small>{displayRole}</small>
            </span>
          )}
        </Link>
      </aside>

      <main className="ms-main">
        <header className="ms-topbar">
          <button
            type="button"
            className="ms-rail-toggle"
            onClick={() => setRailOpen((v) => !v)}
            aria-label="Menyu"
            aria-expanded={railOpen}
          >
            <Icon icon="solar:hamburger-menu-outline" />
          </button>

          <div className="ms-header-where">
            {crumb.group && <span>{crumb.group}</span>}
            <b>{crumb.title}</b>
          </div>

          <div className="ms-topbar-spacer" />

          <button
            type="button"
            className="ms-cmdk-trigger"
            onClick={() => setPaletteOpen(true)}
            aria-label="Tezkor qidiruv (Ctrl+K)"
          >
            <Icon icon="tabler:search" />
            <span>Qidirish…</span>
            <kbd>Ctrl K</kbd>
          </button>


          <div className="ms-profile-wrap" ref={profileWrap}>
            <button
              className="ms-profile"
              type="button"
              aria-haspopup="menu"
              aria-expanded={profileOpen}
              onClick={() => setProfileOpen((prev) => !prev)}
            >
              {loading ? (
                <div className="ms-avatar-skeleton" />
              ) : (
                <Avatar name={displayName} />
              )}
              <span className="ms-profile-meta">
                <b>{displayName}</b>
                <span>{displayRole}</span>
              </span>
            </button>

            {profileOpen && (
              <div className="ms-pop ms-pop--profile" role="menu">
                <div className="ms-pop-identity">
                  <Avatar name={displayName} />
                  <span>
                    <b>{displayName}</b>
                    <span>{loading ? "" : user?.email || ""}</span>
                  </span>
                </div>

                <div className="ms-pop-sep" />

                <Link className="ms-pop-item" role="menuitem" to="/profil">
                  <Icon icon="tabler:user" />
                  Mening profilim
                </Link>

                <button
                  type="button"
                  className="ms-pop-item is-danger"
                  role="menuitem"
                  onClick={signOut}
                >
                  <Icon icon="tabler:power" />
                  Chiqish
                </button>
              </div>
            )}
          </div>
        </header>

        <CommandPalette open={paletteOpen} onClose={closePalette} pages={pages} />

        <div className="dashboard-main-body">
          {loading ? (
            <div className="ms-panel">
              <div className="ms-state">
                <div className="ms-spinner" />
              </div>
            </div>
          ) : !grant ? (
            // A cabinet login exists but has not been granted a system yet.
            // Say so plainly -- otherwise every page is an empty table and it
            // reads as a bug.
            <div className="ms-panel">
              <div className="ms-state">
                <Icon icon="tabler:plug-connected-x" />
                <span>
                  <b>Hisobingiz hali biror tizimga biriktirilmagan.</b>
                  <br />
                  Sozlashni yakunlash uchun MySuhbat jamoasiga murojaat qiling.
                </span>
              </div>
            </div>
          ) : (
            // Pages read the account through useClient(), which takes it from
            // here rather than asking /me/ a second time.
            <Outlet context={{ user }} />
          )}
        </div>

      </main>
    </section>
  );
};

export default MasterLayout;
